define((require) => {
    "use strict";

    const ImprovedModel = require("shared/zbone/ImprovedModel");
    const Rectangle     = require("shared/util/Rectangle");
    const Preconditions = require("shared/util/Preconditions");

    const DIRECTION_FORWARD = "forward";
    const DIRECTION_REVERSE = "reverse"; // TODO: bad naming, should be something like BACKWARDS to support DIRECTION_UPWARDS/DOWNWARDS eventually

    /**
     * View model for the experience viewer.
     *
     * @class ExperienceViewModel
     * @constructor
     */
    return ImprovedModel.extend("ExperienceViewModel", { // we require "computed" functionality to handle stalled/paused state

        immutable: true, // not a persisted model!

        defaults: {
            /**
             * @type {ExperienceModel}
             * The Experience in question.
             */
            experience: null,
            /**
             * @type {Number}
             * The current scene shown for the Experience.
             */
            currentScene: 1,
            /**
             * @type {Boolean}
             * Only set to true to trigger a close of the Experience/Viewer. Once this set to true, if ever, this
             * Viewer is permanently closed (destroyed).
             */
            closed: false,
            /**
             * @type {Boolean}
             * True when the tab containing the Experience/Viewer is shown in the browser, false otherwise. Even
             * when shown, the Experience/Viewer may be not be visible to the user, e.g. if embedded on an adjacent
             * Scene. <p>
             *
             * This will be set for non-embedded Viewers only - we only listen at the top level - as a change here
             * will trigger a corresponding recursive "change:active" event.
             */
            shown: true,
            /**
             * @type {Boolean}
             * True if the Experience is currently visible (in the Browser viewport/DOM), false otherwise. When
             * visible, the Experience may currently not be visible to the end user if she is viewing a different
             * tab, for example, in the browser.
             */
            visible: true,
            /**
             * @type {Boolean}
             * Whether or not the Viewer is active "as a whole" or not. When active, only a single Scene will be
             * active at a time - the current Scene. Once the user (or auto play) transitions to the next Scene,
             * the previous Scene becomes inactive and the new Scene becomes active. When the Viewer is inactive,
             * all Scenes are inactive. <p>
             *
             * Inactive Scenes may be rendered differently and will not listen for events (in affect "stalled").
             * As a whole, an inactive Viewer/Experience will not listen for global events like "resize" or "scroll". <p>
             *
             * Normally, a Viewer/Experience is always active, with a single active Scene. Embedded Viewers/Experiences,
             * however, may or may not be inactive when they are embedded. Viewers/Experiences embedded on adjacent
             * Scenes will be tagged as inactive as they should not influence the current Scene. When such a Scene is
             * transitioned into the current Scene, the embedded Viewer/Experience will be tagged as active, and the
             * previous Scene as inactive, recursing into possible embedded Viewers/Experiences embedded there, and so
             * forth. <p>
             *
             * Embedded Viewers/Experiences on the same Scene will not be tagged as inactive when the embedding Scene
             * is active, as they are part of the current content for the Scene. On transition, they will like always
             * be set as inactive. <p>
             *
             * Note that an active Viewer can be stalled, i.e. ignores mouse events but still listens for resize
             * events, for example during an open Lightbox.
             *
             * @see ResponsiveViewerController
             */
            active: true,
            /**
             * @type {Boolean}
             * True if the Experience is stale with respect to mouse events, false otherwise. A stale Experience
             * will always be paused, but a paused Experience need not be stale. For example, a paused Experience
             * will resume auto play, if so configured, once the user triggers a mouse leave on it, but if also stale,
             * such mouse events are ignored, ensuring the Experience remains "where it is in time". This is typically
             * needed when lightboxes are opened. <p>
             *
             * Once this value is set to true, it will trigger <tt>paused</tt> being set to true as well, but not
             * the reverse.
             *
             * @see #paused
             * @see #autoPlay
             */
            stalled: false,
            /**
             * @type {Boolean}
             * True if the Experience is currently paused (with regards to auto play), false otherwise. An Experience
             * is typically paused when the user interacts with the Experience, including on mouse hover, and normally
             * resumes auto play on for example mouse leave. However, if the Experience is also tagged as "stale",
             * such mouse events will be ignored, ensuring the Experience remains "where it is in time".
             *
             * @see #stalled
             * @see #autoPlay
             */
            paused: false,
            /**
             * @type {Boolean}
             * True when auto play is enabled (default), false otherwise. Once a user interacts directly with an
             * Experience via an Action set to disable auto play, auto play will be disabled henceforth for it,
             * but it will not affect other Experiences, neither parents, siblings, or children (embedded) ones.
             * This means that individual actions cannot re-enable auto play again. <p>
             *
             * When false, the Experience can still be stalled/unstalled and/or paused/unpaused, but unpausing will
             * thus not start auto play.
             *
             * @see #paused
             */
            autoPlay: true, // true here, not false like on Action by default!
            /**
             * @type {String}
             * Auto play direction, as an enum string, e.g. "forward" or "reverse".
             *
             * @see #DIRECTION_FORWARD
             * @see #DIRECTION_REVERSE
             */
            direction: "forward",
            /**
             * @type {Rectangle}
             * The current viewport size.
             */
            viewportVisibility: new Rectangle(0, 0, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY),
            /**
             * @type {Action[]}
             * Array of pending Actions, such as a lightbox that has been activated but not closed, to ensure
             * such pending actions will be forcefully closed when the Experience Viewer (and thus this model)
             * is destroyed.
             *
             * @see Action#pendingEvent
             * @see Action#activate
             */
            pendingActions: [],
            /**
             * @type {CompanySettings}
             * this is the Company Settings for Classic Viewer
             */
            companySettings: {}
        },

        computed: {
            _paused: { // ensures that changes to "paused" while stalled will be reflected to be restored when no longer stalled...
                deps: ["paused"],
                value(paused, options) {
                    // Make sure "_paused" is only updated while "stalled" is true...
                    if (this.get("stalled")) {
                        // Make sure we only initialise "_paused" based on it being stalled, not because somebody
                        // sets this concurrently while stalled...
                        if (options.stalled) {
                            return true;

                        // Update "_paused" if already defined (from the if-statement above sometime in the past)...
                        } else if (this.has("_paused")) {
                            return paused;
                        }
                    }
                    // undefined
                },
                silent: true
            }
        },

        /**
         * Gets and/or sets the named state on this view model, potentially triggering recursive changes to other
         * related Viewers. On set, the previous state is returned.
         *
         * @param {String}   name       The state name: "shown", "visible", "stalled", "paused", "autoPlay", or "closed".
         * @param {Boolean} [value]     The state value to set, if any; can be undefined.
         * @param {Boolean} [recursive] True if recursive, false otherwise.
         *
         * @return {Boolean} The (previous) state value; never undefined.
         */
        state(name, value, recursive) { // for example used to pause embedded experiences
            let val = false;
            // Set?
            if (typeof value !== "undefined") {
                val = this.get(name);
                // Disabled auto play cannot be re-enabled for *this* Experience...
                if (name === "autoPlay") {
                    value = val && value;
                    recursive = false;
                }
                this.set(name, value, {recursive});

            // Get!
            } else {
                val = this.get(name);
            }
            return val;
        },

        /**
         * Gets the scene ID of the current scene.
         * @returns {String} The scene ID of the current scene.
         */
        getCurrentSceneId() {
            Preconditions.checkState(this.has("experience"), "No experience defined");
            const experience = this.get("experience");
            const currentSceneNum = this.get("currentScene");
            const currentScene = experience.getSceneBySceneNumber(currentSceneNum);
            return currentScene.get("id");
        },

        /**
         * Updates the model to point to the specified <tt>sceneNumber</tt>.
         *
         * @param  {Number} sceneNumber The scene number.
         */
        goToScene(sceneNumber) {
            const totalNumberOfScenes = this.get("experience").get("scenes").length;
            const currentSceneNumber = this.get("currentScene");

            sceneNumber = Math.min(totalNumberOfScenes, Math.max(1, sceneNumber));

            this.set({
                direction: sceneNumber > currentSceneNumber ? "forward" : "reverse",
                currentScene: sceneNumber
            });
        },

        /**
         * Updates the model to point at the next scene, wrapping around if the current scene is at the end of the
         * experience.
         */
        goToNextScene() {
            this.set("direction", "forward");
            this.set("currentScene", this.getNextScene());
        },

        /**
         * Gets the scene number after the current scene, which may wrap around to the first scene.
         * @returns {Number} The scene number after the current scene.
         */
        getNextScene() {
            let nextScene = this.get("currentScene") + 1;

            if (nextScene > this.get("experience").get("scenes").length) {
                nextScene = 1;
            }

            return nextScene;
        },

        /**
         * Gets the scene number before the current scene, which may wrap around to the last scene.
         * @returns {Number} The scene number before the current scene.
         */
        getPreviousScene() {
            let previousScene = this.get("currentScene") - 1;

            if (previousScene < 1) {
                previousScene = this.get("experience").get("scenes").length;
            }

            return previousScene;
        },

        /**
         * Updates the model to point at the previous scene, wrapping around if the current scene is at the start of the
         * experience.
         */
        goToPreviousScene() {
            this.set("direction", "reverse");
            this.set("currentScene", this.getPreviousScene());
        }

    }, {

        DIRECTION_FORWARD,
        DIRECTION_REVERSE

    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/viewer/ExperienceViewModel.js